import { App as CapacitorApp } from '@capacitor/app';

import { api } from '@/api/client';
import { isStoreBuild } from '@/lib/platform';

import {
  CRASH_MESSAGE_MAX,
  CRASH_PER_LAUNCH,
  CRASH_STACK_MAX,
  createCrashLimiter,
  crashFingerprint,
  describeError,
  isCrashNoise,
  launchStateForActive,
  markLaunch,
  readLaunch,
  scrubCrashText,
  scrubRoute,
  startLaunch,
  writeLaunch,
  type CrashKind,
} from './crashReport';

/**
 * Отчёты о падениях приложения — подключение (T23). Правила — `lib/crashReport.ts`.
 *
 * ⚠️ ОТПРАВКА ТОЛЬКО ЧЕРЕЗ `api.post`. Там адаптер `nativeSafeAdapter`: запрос идёт нативным HTTP с
 * закреплённым сертификатом (pinning) и UA приложения — по UA сервер узнаёт версию и систему. `sendBeacon`
 * и голый `fetch` прошли бы мимо закрепления и без метки приложения.
 *
 * ⚠️ ОТЧЁТ НЕ ДОЛЖЕН РОНЯТЬ НИЧЕГО. Каждая ветка — в try/catch, неудачная отправка не порождает нового
 * отчёта, а откладывается до следующего запуска.
 */

const PENDING_KEY = 'onebilim.crashPending';

type CrashPayload = {
  kind: CrashKind;
  message: string;
  stack: string;
  route: string;
  app_version: string;
  app_build: string;
};

let started = false;
let limiter = createCrashLimiter();
let appVersion = '';
let appBuild = '';

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readPending(s: Storage): CrashPayload[] {
  try {
    const v = JSON.parse(s.getItem(PENDING_KEY) || '[]');
    return Array.isArray(v) ? (v as CrashPayload[]).slice(0, CRASH_PER_LAUNCH) : [];
  } catch {
    return [];
  }
}

function savePending(s: Storage, list: CrashPayload[]): void {
  try {
    if (list.length) s.setItem(PENDING_KEY, JSON.stringify(list.slice(0, CRASH_PER_LAUNCH)));
    else s.removeItem(PENDING_KEY);
  } catch {
    /* хранилище недоступно — отчёт потерян, и только */
  }
}

async function send(p: CrashPayload): Promise<void> {
  try {
    await api.post('/app/crash-report', p);
  } catch {
    // Нет сети или сервер не ответил — отложить до следующего запуска, без очереди outbox.
    const s = storage();
    if (s) savePending(s, [...readPending(s), p]);
  }
}

function flushPending(): void {
  const s = storage();
  if (!s) return;
  const list = readPending(s);
  if (!list.length) return;
  savePending(s, []);
  list.forEach((p) => void send(p));
}

/** Отправить отчёт. На сайте — ничего; не больше пяти за запуск; одно и то же — один раз. */
export function reportCrash(kind: CrashKind, err: unknown, prev?: { version: string; build: string }): void {
  try {
    if (!isStoreBuild()) return;
    const { message: rawMessage, stack: rawStack } = describeError(err);
    if (kind !== 'previous-launch-crash' && isCrashNoise(err, rawMessage)) return;
    const message = scrubCrashText(rawMessage).slice(0, CRASH_MESSAGE_MAX);
    const stack = scrubCrashText(rawStack).slice(0, CRASH_STACK_MAX);
    if (!limiter.take(crashFingerprint(kind, message, stack))) return;
    void send({
      kind,
      message,
      stack,
      route: kind === 'previous-launch-crash' ? '' : scrubRoute(window.location.pathname),
      app_version: prev?.version ?? appVersion,
      app_build: prev?.build ?? appBuild,
    });
  } catch {
    /* отчёт не должен ронять ничего */
  }
}

/** Ставится в `main.tsx` до первой отрисовки. На сайте ничего не делает. */
export function initCrashReporter(): void {
  if (started || !isStoreBuild()) return;
  started = true;
  limiter = createCrashLimiter();
  const s = storage();
  // Читаем прошлый запуск ДО того, как пометить этот: иначе свой же флаг затёр бы улику.
  const prevLaunch = s ? startLaunch(s, '', '') : null;

  window.addEventListener('error', (e) => reportCrash('js-error', e.error ?? e.message));
  window.addEventListener('unhandledrejection', (e) => reportCrash('unhandled', e.reason));
  window.addEventListener('pagehide', () => {
    if (s) markLaunch(s, 'clean');
  });
  // ⚠️ Именно appStateChange (iOS: willResignActive / didBecomeActive), не 'pause' (только уход в фон): смахивание
  // из переключателя приложений бывает без ухода в фон и не должно считаться падением.
  CapacitorApp.addListener('appStateChange', ({ isActive }) => {
    if (s) markLaunch(s, launchStateForActive(isActive));
  }).catch(() => undefined);

  void CapacitorApp.getInfo()
    .then((info) => {
      appVersion = info.version ?? '';
      appBuild = info.build ?? '';
      if (s) writeLaunch(s, { state: readLaunch(s)?.state ?? 'running', version: appVersion, build: appBuild });
    })
    .catch(() => undefined)
    .finally(() => {
      if (prevLaunch) reportCrash('previous-launch-crash', 'previous launch did not finish', prevLaunch);
      flushPending();
    });
}
